/**
 * Route admin `/tutor/admin/animations/$animationId/studio` — le Studio
 * d'une animation (moteur JRAF, lot 5) : modifier le brouillon par
 * formulaires, avec l'aperçu de la scène à l'instant choisi, enregistré tout
 * seul. « Tester » le joue comme un élève ; « Publier » y reste.
 */

import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Loader2, Play } from 'lucide-react';
import { AnimationStudio } from '@/features/animations/studio/animation-studio';
import { GENERATION_STEP_LABELS, isGenerating, useAnimation } from '@/lib/animations/api';
import { useCardTools } from '@/lib/cards/card-tools';

export function TutorAdminAnimationStudioPage() {
  const { animationId } = useParams({ strict: false }) as { animationId: string };
  const query = useAnimation(animationId);
  const row = query.data;
  const tools = useCardTools(row?.card_id ?? '');

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <div className="mb-2 flex flex-wrap items-center gap-x-4">
        <Link
          to="/tutor/admin/animations/$animationId"
          params={{ animationId }}
          className="text-primary-600 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Tester et publier
        </Link>
      </div>
      <h1 className="font-display text-ink-900 mb-3 text-[22px] font-extrabold">
        Studio{row?.title ? ` — ${row.title}` : ''}
      </h1>

      {query.isLoading && <p className="text-ink-500 text-sm">Chargement…</p>}
      {query.error && (
        <p role="alert" className="text-error-600 text-sm">
          Impossible de charger l’animation.
        </p>
      )}
      {!query.isLoading && !row && !query.error && (
        <p className="text-ink-500 text-sm">Animation introuvable.</p>
      )}
      {row && isGenerating(row) && (
        <p role="status" className="text-ink-700 flex items-center gap-2 text-sm">
          <Loader2 className="text-primary-600 h-4 w-4 animate-spin" aria-hidden />
          {GENERATION_STEP_LABELS[row.generation_step ?? ''] ?? 'Génération en cours'}… Le Studio
          s’ouvre quand l’IA a fini.
        </p>
      )}
      {row && !isGenerating(row) && row.draft == null && (
        <p className="text-ink-500 text-sm">Pas encore de brouillon à modifier.</p>
      )}
      {row && !isGenerating(row) && row.draft != null && tools.data && (
        <AnimationStudio key={row.id} row={row} toolIds={tools.data} />
      )}
      {row && (
        <Link
          to="/tutor/admin/animations/$animationId"
          params={{ animationId }}
          className="bg-primary-500 hover:bg-primary-600 mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold text-white"
        >
          <Play className="h-4 w-4" aria-hidden /> Tester comme un élève
        </Link>
      )}
    </div>
  );
}
